import React, { useState, useRef, useEffect, useCallback } from 'react';
import projectsData from '../../data/projects.json';
import { ProjectCard } from './components/ProjectCard';
import { ProjectDetailStage } from './components/ProjectDetailStage';
import { AchievementFooter } from '../achievements/components/AchievementFooter';
import { useProjectScale } from './useProjectScale';
import type { ProjectItem, ProjectCategory, ProjectsProps } from './projects.types';
import './Projects.styles.css';

const { projectCategories, projects } = projectsData as {
  projectCategories: ProjectCategory[];
  projects: ProjectItem[];
};

/**
 * Projects showcase feature component.
 * Features top floating category pills, DPI-aware dynamic grid, and master-detail staging.
 */
export const Projects: React.FC<ProjectsProps> = ({
  className = '',
  isActive = true,
  scale = 1,
}) => {
  const { containerRef } = useProjectScale();
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);
  const [isCardHovered, setIsCardHovered] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  const [isRecruiterActive, setIsRecruiterActive] = useState<boolean>(false);

  useEffect(() => {
    const handleRecruiterChange = (e: Event): void => {
      const detail = (e as CustomEvent)?.detail;
      if (detail && typeof detail.active === 'boolean') {
        setIsRecruiterActive(detail.active);
      }
    };
    window.addEventListener('recruiter-mode-change', handleRecruiterChange);
    return () => window.removeEventListener('recruiter-mode-change', handleRecruiterChange);
  }, []);

  const gridRef = useRef<HTMLDivElement | null>(null);
  const [visibleIndices, setVisibleIndices] = useState<Set<number>>(new Set());

  // * Filter projects according to the active category
  const filteredProjects =
    activeFilter === 'all'
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  // * Calculate cards per page: Mobile is fixed 2x1 (2 cards), Desktop is dynamic 3x2 (6 cards)
  const getCardsPerPage = useCallback((): number => {
    if (typeof window === 'undefined') return 6;
    if (window.innerWidth < 768) return 2;
    if (window.innerWidth < 1100) return 4;
    return 6;
  }, []);

  const [cardsPerPage, setCardsPerPage] = useState<number>(getCardsPerPage);
  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / cardsPerPage));
  const currentProjects = filteredProjects.slice(
    (currentPage - 1) * cardsPerPage,
    currentPage * cardsPerPage
  );

  // * Responsive resize handler
  useEffect(() => {
    const handleResize = (): void => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setCardsPerPage(getCardsPerPage());
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [getCardsPerPage]);

  // * Reset to first page & collapse active expansion on category filter switch
  useEffect(() => {
    setCurrentPage(1);
    setSelectedProject(null);
  }, [activeFilter]);

  // * Background blur effect: toggles .project-bg-blur on body when card is hovered or expanded AND section is active
  useEffect(() => {
    const shouldBlur = Boolean(isActive && (isCardHovered || selectedProject !== null));
    document.body.classList.toggle('project-bg-blur', shouldBlur);

    return () => {
      document.body.classList.remove('project-bg-blur');
    };
  }, [isActive, isCardHovered, selectedProject]);

  // * Auto-dismiss detail expansion when leaving the Projects section
  useEffect(() => {
    if (!isActive) {
      setSelectedProject(null);
      setIsCardHovered(false);
    }
  }, [isActive]);

  // * Intersection Observer for card entrance reveals
  useEffect(() => {
    const el = gridRef.current;
    if (!el || selectedProject !== null) return;

    setVisibleIndices(new Set());
    const allIndices = currentProjects.map((_, i) => i);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleIndices(new Set(allIndices));
        }
      },
      { threshold: 0.1, rootMargin: '40px 0px 40px 0px' }
    );

    observer.observe(el);
    const timer = setTimeout(() => setVisibleIndices(new Set(allIndices)), 250);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [currentProjects.length, activeFilter, currentPage, selectedProject]);

  // * Page navigation handlers
  const handlePrevPage = (): void => {
    setCurrentPage((p) => Math.max(p - 1, 1));
  };

  const handleNextPage = (): void => {
    setCurrentPage((p) => Math.min(p + 1, totalPages));
  };

  // * Sibling projects for the left-side mini rail during expansion
  const siblingProjects = selectedProject
    ? currentProjects.filter((p) => p.id !== selectedProject.id)
    : [];

  return (
    <section
      ref={containerRef}
      className={`projects-section ${selectedProject ? 'has-expanded' : ''} ${className}`}
      id="projects"
    >
      <div
        className="projects-container"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: isMobile ? 'top center' : 'center center',
          transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* * Top Floating Pill Category Bar */}
        <nav className="top-category-container" aria-label="Project Category Filters">
          <div className="category-track">
            {projectCategories.map((category) => (
              <button
                type="button"
                key={category.id}
                className={`category-pill ${activeFilter === category.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(category.id)}
                aria-pressed={activeFilter === category.id}
              >
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* * Conditional Rendering: Expanded Master-Detail Stage vs Default Grid */}
        {selectedProject ? (
          <ProjectDetailStage
            project={selectedProject}
            siblingProjects={siblingProjects}
            isMobile={isMobile}
            onSelectSibling={(proj) => setSelectedProject(proj)}
            onClose={() => setSelectedProject(null)}
          />
        ) : (
          <div ref={gridRef} className="projects-grid">
            {currentProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                isVisible={visibleIndices.has(index)}
                onSelect={(proj) => setSelectedProject(proj)}
                onHoverChange={setIsCardHovered}
              />
            ))}
          </div>
        )}

        {/* * Pagination Controls: Hidden during active card expansion */}
        {!selectedProject && totalPages > 1 && (
          <div className="pagination-controls" aria-label="Projects Pagination">
            <button
              type="button"
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              aria-label="Previous Page"
            >
              <i className="fas fa-chevron-left" />
            </button>

            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              aria-label="Next Page"
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        )}

        {/* * When recruiter mode is disabled, Projects is final section: display compact footer */}
        {!isRecruiterActive && (
          <div className="projects-footer-wrapper">
            <AchievementFooter isRecruiterActive={false} />
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
